import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getBalance,
  getTiers,
  createCheckout,
  quickPurchase,
  getSavedCard,
  addCard,
  updateCard,
  removeSavedCard,
  configureAutoReload,
  getTransactions
} from '../../apiLayer/dashBoardAPIs/paygAPIs';
import { getUserInfoFromAuth } from '../../apiLayer/vsCodeAuthAPIs/authAPIs';

// Helper to check if error is an authorization error
const isAuthError = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

export const usePAYGDashboard = () => {
  // User info
  const [userInfo, setUserInfo] = useState(null);

  // Balance and account info
  const [balance, setBalance] = useState(null);

  // Purchase tiers
  const [tiers, setTiers] = useState(null);

  // Saved card info
  const [savedCard, setSavedCard] = useState(null);

  // Transaction history
  const [transactions, setTransactions] = useState(null);
  const [transactionsHasMore, setTransactionsHasMore] = useState(false);
  const transactionsRef = useRef(null);

  // Loading states - separate for each section
  const [initialLoading, setInitialLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  const [error, setError] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Track if already initialized to prevent double calls
  const initialized = useRef(false);

  // Keep transactionsRef in sync
  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  // Fetch user info from auth
  const fetchUserInfo = useCallback(async () => {
    try {
      const data = await getUserInfoFromAuth();
      setUserInfo(data);
      return data;
    } catch (err) {
      console.error('Error fetching user info:', err);
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
      throw err;
    }
  }, []);

  // Fetch balance
  const fetchBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const data = await getBalance();
      setBalance(data);
      return data;
    } catch (err) {
      console.error('Error fetching balance:', err);
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Fetch tiers
  const fetchTiers = useCallback(async () => {
    setTiersLoading(true);
    try {
      const data = await getTiers();
      setTiers(data);
      return data;
    } catch (err) {
      console.error('Error fetching tiers:', err);
    } finally {
      setTiersLoading(false);
    }
  }, []);

  // Fetch saved card
  const fetchSavedCard = useCallback(async () => {
    setCardLoading(true);
    try {
      const data = await getSavedCard();
      setSavedCard(data);
      return data;
    } catch (err) {
      console.error('Error fetching saved card:', err);
      setSavedCard({ has_saved_card: false });
    } finally {
      setCardLoading(false);
    }
  }, []);

  // Fetch transactions - no dependency on transactions state
  const fetchTransactions = useCallback(async (limit = 50, offset = 0, append = false) => {
    setTransactionsLoading(true);
    try {
      const data = await getTransactions(limit, offset);
      if (append && transactionsRef.current) {
        const newData = {
          ...data,
          transactions: [...transactionsRef.current.transactions, ...data.transactions]
        };
        setTransactions(newData);
      } else {
        setTransactions(data);
      }
      setTransactionsHasMore(data.has_more);
      return data;
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  // Load more transactions
  const loadMoreTransactions = useCallback(async () => {
    if (!transactionsRef.current) return;
    const offset = transactionsRef.current.transactions.length;
    await fetchTransactions(50, offset, true);
  }, [fetchTransactions]);

  // Create checkout session
  const handleCreateCheckout = useCallback(async (amount) => {
    setPurchaseLoading(true);
    try {
      const successUrl = `${window.location.origin}/payg-dashboard?success=true`;
      const cancelUrl = `${window.location.origin}/payg-dashboard?cancelled=true`;
      const data = await createCheckout(amount, successUrl, cancelUrl);

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
      return data;
    } catch (err) {
      console.error('Error creating checkout:', err);
      throw err;
    } finally {
      setPurchaseLoading(false);
    }
  }, []);

  // Add card (without purchase)
  const handleAddCard = useCallback(async () => {
    setActionLoading(true);
    try {
      const successUrl = `${window.location.origin}/payg-dashboard?card_added=true`;
      const cancelUrl = `${window.location.origin}/payg-dashboard`;
      const data = await addCard(successUrl, cancelUrl);

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
      return data;
    } catch (err) {
      console.error('Error adding card:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Quick purchase
  // For tier purchase: handleQuickPurchase('TIER_25')
  // For custom amount: handleQuickPurchase(null, 15.00)
  const handleQuickPurchase = useCallback(async (tier = null, customAmount = null) => {
    setPurchaseLoading(true);
    try {
      // Call with either tier OR amount, not both
      const data = tier
        ? await quickPurchase(tier, null)
        : await quickPurchase(null, customAmount);

      if (data.success) {
        // Refresh balance and transactions after successful purchase
        fetchBalance();
        fetchTransactions();
      }

      return data;
    } catch (err) {
      console.error('Error with quick purchase:', err);
      throw err;
    } finally {
      setPurchaseLoading(false);
    }
  }, [fetchBalance, fetchTransactions]);

  // Update card
  const handleUpdateCard = useCallback(async () => {
    setActionLoading(true);
    try {
      const successUrl = `${window.location.origin}/payg-dashboard?card_updated=true`;
      const cancelUrl = `${window.location.origin}/payg-dashboard`;
      const data = await updateCard(successUrl, cancelUrl);

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
      return data;
    } catch (err) {
      console.error('Error updating card:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Remove card
  const handleRemoveCard = useCallback(async () => {
    setActionLoading(true);
    try {
      const data = await removeSavedCard();
      await fetchSavedCard();
      return data;
    } catch (err) {
      console.error('Error removing card:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [fetchSavedCard]);

  // Configure auto-reload
  const handleConfigureAutoReload = useCallback(async (enabled, threshold = null, reloadAmount = null) => {
    setActionLoading(true);
    try {
      const data = await configureAutoReload(enabled, threshold, reloadAmount);
      await fetchBalance(); // Refresh balance to get updated auto-reload settings
      return data;
    } catch (err) {
      console.error('Error configuring auto-reload:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [fetchBalance]);

  // Initialize dashboard - run only once
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const init = async () => {
      setInitialLoading(true);
      setError(null);

      try {
        // Fetch critical data first (user info + balance)
        await fetchUserInfo();

        // Show the page and load balance immediately
        fetchBalance();

        // Load secondary data in background (don't await)
        fetchTiers();
        fetchSavedCard();
        fetchTransactions();

      } catch (err) {
        console.error('Dashboard initialization failed:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    init();
  }, [fetchUserInfo, fetchBalance, fetchTiers, fetchSavedCard, fetchTransactions]);

  // Refresh data (used after URL params indicate success)
  const refreshData = useCallback(() => {
    fetchBalance();
    fetchSavedCard();
    fetchTransactions();
  }, [fetchBalance, fetchSavedCard, fetchTransactions]);

  return {
    // Data
    userInfo,
    balance,
    tiers,
    savedCard,
    transactions,
    transactionsHasMore,

    // Loading states
    loading: initialLoading,
    balanceLoading,
    tiersLoading,
    cardLoading,
    transactionsLoading,
    error,
    purchaseLoading,
    actionLoading,

    // Methods
    fetchBalance,
    fetchTiers,
    fetchSavedCard,
    fetchTransactions,
    loadMoreTransactions,
    handleCreateCheckout,
    handleAddCard,
    handleQuickPurchase,
    handleUpdateCard,
    handleRemoveCard,
    handleConfigureAutoReload,
    refreshData
  };
};
